import React, { useEffect, useState } from "react";
import Header from "../../../components/Header";
import { routes } from "../../../common/svg";
import AdminCointainer from "../../Admin";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RoutesFormValues } from "../../../service/interfaces/Forms";
import { validationSchema } from "../../../service/validation/routes";
import DropDownMap from "../../../components/AuthMap/DropDownMap";
import CustomButton from "../../../components/AddButton";
import Input from "../../../components/FormInputs";
import { ApiService } from "../../../service/api.service";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MASTER_URL } from "../../../common/common";
import RouteMap from "../../../components/RouteMap/RouteMap";

const ManageRoutes = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const apiService = new ApiService();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [routeData, setRouteData] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<RoutesFormValues>({
    resolver: yupResolver(validationSchema) as any,
  });

  // Watch for changes to the form values to update the map
  const pickupPoint = watch("pickupPoint");
  const loadingPoint = watch("loadingPoint");
  const unloadingPoint = watch("unloadingPoint");
  const clearancePoint = watch("clearancePoint");

  // Load route data for editing
  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchRouteData();
    }
  }, [id]);

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      const response: any = await apiService.getApi(
        `${MASTER_URL}/route/get-one-route?routeId=${id}`
      );

      if (response.status === "200") {
        const routeData = response.body;
        setRouteData(routeData);
        reset({
          name: routeData.name,
          pickupPoint: {
            address: routeData.pickupPoint,
            lat: routeData.pickupLatitude,
            lng: routeData.pickupLongitude
          },
          loadingPoint: {
            address: routeData.loadingPoint,
            lat: routeData.loadingLatitude,
            lng: routeData.loadingLongitude
          },
          unloadingPoint: {
            address: routeData.unloadingPoint,
            lat: routeData.unloadingLatitude,
            lng: routeData.unloadingLongitude
          },
          clearancePoint: {
            address: routeData.clearancePoint,
            lat: routeData.clearanceLatitude,
            lng: routeData.clearanceLongitude
          }
        });
      }
    } catch (error) {
      console.error("Error fetching route data:", error);
    } finally {
      setLoading(false);
    }
  };

  const Submit = async (data: any) => {
    try {
      console.log(data);
      if (data) {
        const transformedData: any = {
          name: data.name,
          pickupPoint: data.pickupPoint?.address || '',
          loadingPoint: data.loadingPoint?.address || '',
          unloadingPoint: data.unloadingPoint?.address || '',
          clearancePoint: data.clearancePoint?.address || '',
          pickupLatitude: data.pickupPoint?.lat?.toString() || '0',
          pickupLongitude: data.pickupPoint?.lng?.toString() || '0',
          loadingLatitude: data.loadingPoint?.lat?.toString() || '0',
          loadingLongitude: data.loadingPoint?.lng?.toString() || '0',
          clearanceLatitude: data.clearancePoint?.lat?.toString() || '0',
          clearanceLongitude: data.clearancePoint?.lng?.toString() || '0',
          unloadingLatitude: data.unloadingPoint?.lat?.toString() || '0',
          unloadingLongitude: data.unloadingPoint?.lng?.toString() || '0',
        };

        if (isEditMode && id) {
          // Edit mode
          transformedData.routeId = id;
          const response: any = await apiService.putApi(`${MASTER_URL}/route/edit-route`, transformedData);
          if (response && response.status === "200") {
            // Check for return URL, otherwise go to routes list
            const returnUrl = searchParams.get('returnUrl');
            navigate(returnUrl ? decodeURIComponent(returnUrl) : "/routes");
          } else {
            console.error("Failed to update route");
          }
        } else {
          // Add mode
          const response: any = await apiService.postApi(`${MASTER_URL}/route/store-route`, transformedData);
          if (response && response.status === "200") {
            // Check for return URL, otherwise go to routes list
            const returnUrl = searchParams.get('returnUrl');
            navigate(returnUrl ? decodeURIComponent(returnUrl) : "/routes");
          } else {
            console.error("Failed to add route");
          }
        }
      }
    } catch (error) {
      console.error("Error submitting form", error);
    }
  };

  return (
    <div>
      <Header
        title={isEditMode ? "Edit Route" : "Add New Route"}
        icon={routes}
        showSearch={false}
      />
      <AdminCointainer>
        <form onSubmit={handleSubmit(Submit)}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white p-6 rounded-lg shadow-sm">
            <div className="flex flex-col gap-4">
              <Input
                label="Route Name"
                name="name"
                type="text"
                errors={errors}
                register={register}
                placeholder="Enter Route Name"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <DropDownMap
                    errors={errors}
                    register={register}
                    label="Pickup Point *"
                    name="pickupPoint"
                    setValue={setValue}
                    defaultValue={routeData ? {
                      value: routeData.pickupPoint,
                      lat: routeData.pickupLatitude,
                      lng: routeData.pickupLongitude,
                      address: routeData.pickupPoint
                    } : undefined}
                  />
                </div>
                <div>
                  <DropDownMap
                    errors={errors}
                    register={register}
                    label="Loading Point *"
                    name="loadingPoint"
                    setValue={setValue}
                    defaultValue={routeData ? {
                      value: routeData.loadingPoint,
                      lat: routeData.loadingLatitude,
                      lng: routeData.loadingLongitude,
                      address: routeData.loadingPoint
                    } : undefined}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <DropDownMap
                    errors={errors}
                    register={register}
                    label="Clearance Point *"
                    name="clearancePoint"
                    setValue={setValue}
                    defaultValue={routeData ? {
                      value: routeData.clearancePoint,
                      lat: routeData.clearanceLatitude,
                      lng: routeData.clearanceLongitude,
                      address: routeData.clearancePoint
                    } : undefined}
                  />
                </div>
                <div>
                  <DropDownMap
                    errors={errors}
                    register={register}
                    label="Unloading Point *"
                    name="unloadingPoint"
                    setValue={setValue}
                    defaultValue={routeData ? {
                      value: routeData.unloadingPoint,
                      lat: routeData.unloadingLatitude,
                      lng: routeData.unloadingLongitude,
                      address: routeData.unloadingPoint
                    } : undefined}
                  />
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-end gap-3">
                  <CustomButton
                    buttonClassName="!w-[100px] !h-[40px] !bg-gray-500 hover:!bg-gray-600"
                    type="button"
                    label="Cancel"
                    onClick={() => {
                      const returnUrl = searchParams.get('returnUrl');
                      navigate(returnUrl ? decodeURIComponent(returnUrl) : "/routes");
                    }}
                  />
                  <CustomButton
                    buttonClassName="!w-[100px] !h-[40px]"
                    type="submit"
                    label="Submit"
                  />
                </div>
              </div>
            </div>

            <div className="h-[450px] rounded-lg overflow-hidden">
              <RouteMap
                pickupPoint={pickupPoint}
                loadingPoint={loadingPoint}
                clearancePoint={clearancePoint}
                unloadingPoint={unloadingPoint}
              />
            </div>
          </div>
        </form>
      </AdminCointainer>
    </div>
  );
};

export default ManageRoutes;